import { createContext, useCallback, useEffect, useRef } from "react"
import { type ShortcutAction, ShortcutKeyMap } from "../utils/shortcuts"

type ShortcutContextType = {
	registerShortcut: (action: ShortcutAction, handler: () => void) => void
}

const ShortcutContext = createContext<ShortcutContextType | undefined>(
	undefined,
)

export { ShortcutContext }

export const ShortcutProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const handlersRef = useRef(new Map<string, () => void>())
	const isShadowInputFocusedRef = useRef(false)

	const isInputFocused = () => {
		let activeElement = document.activeElement

		// Traverse through shadow DOM boundaries to find the actual focused element
		while (activeElement && activeElement.shadowRoot) {
			const shadowActiveElement = activeElement.shadowRoot
				.activeElement as HTMLElement | null
			if (shadowActiveElement) {
				activeElement = shadowActiveElement
			} else {
				break
			}
		}
		if (
			activeElement &&
			(activeElement.tagName === "INPUT" ||
				activeElement.tagName === "TEXTAREA" ||
				(activeElement as HTMLElement).contentEditable === "true")
		) {
			return true
		}
		return isShadowInputFocusedRef.current
	}

	const handleKeyDown = useCallback((event: KeyboardEvent) => {
		// Build key combination string
		const modifiers = []
		if (event.metaKey) modifiers.push("cmd")
		if (event.ctrlKey) modifiers.push("ctrl")
		if (event.altKey) modifiers.push("alt")
		if (event.shiftKey) modifiers.push("shift")

		const key = event.key.toLowerCase()
		const keyCombo =
			modifiers.length > 0 ? `${modifiers.join("+")}+${key}` : key

		// Check if any registered shortcut matches
		for (const [registeredCombo, handler] of handlersRef.current.entries()) {
			const combos = registeredCombo.split("|") // Support multiple key combinations
			if (combos.includes(keyCombo)) {
				// Special handling for single key shortcuts - check if input is focused
				// Exception: escape key should always work even when inputs are focused
				if (
					modifiers.length === 0 &&
					isInputFocused() &&
					key !== "escape" &&
					key !== "enter"
				) {
					return // Don't trigger single key shortcuts when typing in inputs
				}

				event.preventDefault()
				handler()
				return
			}
		}
	}, [])

	const registerShortcut = useCallback(
		(action: ShortcutAction, handler: () => void) => {
			const keyCombo = ShortcutKeyMap[action]
			handlersRef.current.set(keyCombo, handler)
		},
		[],
	)

	useEffect(() => {
		const handleCommandMenuFocus = (event: Event) => {
			const customEvent = event as CustomEvent<{ isFocused: boolean }>
			isShadowInputFocusedRef.current = customEvent.detail.isFocused
		}
		window.addEventListener("keydown", handleKeyDown)
		window.addEventListener("command-menu-focus", handleCommandMenuFocus)
		return () => {
			window.removeEventListener("keydown", handleKeyDown)
			window.removeEventListener("command-menu-focus", handleCommandMenuFocus)
		}
	}, [handleKeyDown])

	return (
		<ShortcutContext.Provider value={{ registerShortcut }}>
			{children}
		</ShortcutContext.Provider>
	)
}
